import {
  useEffect,
  useRef,
  type Dispatch,
  type SetStateAction
} from 'react';
import { Col, Row } from 'react-bootstrap';
import { PagesColumns } from '../config/columnsConfig';
import {
  useCountCrawlPagesQuery,
  useGetCrawlPagesMutation
} from '@/app/Crawl/api/pagesApi';
import {
  useLocation,
  useOutletContext,
  useParams,
  useSearchParams
} from 'react-router-dom';
import { useGetUserInfoQuery } from '@/app/common/api/authApi';
import { useGetTableSettingsQuery } from '@/app/common/api/tableApi';
import { convertToQueryString } from '@/utils/queryString';
import { useExportTableMutation } from '@/app/Crawl/api/exportApi';
import FiltersComponent from '../../common/components/filters/FiltersComponent';
import AgGridWrapper from '@/app/common/components/ag-grid/AgGridWrapper';
import PageDrawer from '../components/pages/PageDrawer';
import NoCrawlData from '../components/common/NoCrawlData';
import AgGridSkeleton from '@/app/common/components/skeleton/AgGridSkeleton';
import DataLoadError from '@/components/errors/DataLoadError';
import { shouldShowDataLoadError, isNetworkApiError } from '@/app/common/utils/apiErrorUtils';
import FiltersComponentSkeleton from '../../common/components/filters/FiltersComponentSkeleton';
import useDomain from '@/hooks/useDomain';

type BreadcrumbItem = {
  text: string;
  href: string;
};

type CrawlOutletContext = [
  BreadcrumbItem[],
  Dispatch<SetStateAction<BreadcrumbItem[]>>
];

type PagesData = {
  crawl_status?: number;
  job_page?: string;
  total?: number;
  rows?: Record<string, any>[];
};

type PagesResponse = {
  data?: PagesData;
};

/* بخش صفحات خزش */
const Pages = () => {
  const [, setBreadcrumbData] = useOutletContext<CrawlOutletContext>();
  useEffect(() => {
    setBreadcrumbData([{ text: 'خزش جدید', href: '#' }]);
  }, []);
  const gridRef = useRef<HTMLDivElement | null>(null);
  const { pathname } = useLocation();
  const [, setSearchParams] = useSearchParams();

  const [getPages] = useGetCrawlPagesMutation();
  const { slug } = useParams();
  const { domain_slug } = useDomain();
  const job_page = slug === 'last' ? domain_slug : slug;
  useGetUserInfoQuery();
  const [exportTable] = useExportTableMutation();

  const {
    data: rawData,
    isLoading,
    isError,
    error,
    refetch,
    isFetching
  } = useCountCrawlPagesQuery(
    { job_page, domain_slug },
    { skip: !job_page || !domain_slug }
  );
  const data = rawData as PagesResponse | undefined;
  const pageData = data?.data;

  /* دریافت تنظیمات ستون های جدول و فیلترها */
  const { data: tableSettings, isLoading: isLoadingSettings } =
    useGetTableSettingsQuery({
      table_name: pathname.split('/').slice(1, 3).join('/'),
      domain_slug: domain_slug
    });

  /* اگر فلیترهای جدول رو ذخیره کرد موقه لود اولیه جدول کویری استرینگ آدرس عوض بشه */
  useEffect(() => {
    if (!isLoadingSettings && tableSettings?.data) {
      const filters = tableSettings?.data.filters as Record<string, unknown>;
      if (
        Object.keys(filters).length !== 0 &&
        !location.search.includes('FBy')
      ) {
        const queryString = convertToQueryString(filters);
        setSearchParams(queryString);
      }
    }
  }, [isLoadingSettings]);

  if (shouldShowDataLoadError(isError, error)) {
    return (
      <DataLoadError
        onRetry={() => refetch()}
        isRetrying={isFetching}
        isNetworkError={isNetworkApiError(error)}
      />
    );
  }
  if (!isLoading && !pageData) return <NoCrawlData />;

  return (
    <Row>
      <Col xxl={12}>
        {isLoadingSettings ? (
          <FiltersComponentSkeleton />
        ) : (
          <FiltersComponent columns={PagesColumns(null)} />
        )}
      </Col>
      <Col>
        {!isLoading && !isLoadingSettings ? (
          <div ref={gridRef}>
            <AgGridWrapper
              table_name={location.pathname.split('/').slice(1, 3).join('/')}
              settings={{
                pagination: true,
                fullscreen: true,
                changeHeight: true,
                resetGrid: true,
                saveGrid: true,
                sideBar: true,
                columnsState: tableSettings?.data,
                export: {
                  enabled: true,
                  fetchFunction: exportTable
                }
              }}
              fetchFunction={getPages}
              height="746px"
              columns={PagesColumns(pageData?.crawl_status)}
              additionalData={{ job_page: pageData?.job_page, domain_slug }}
              total={pageData?.total}
              data={pageData?.rows}
              page_depth={1}
            />
          </div>
        ) : (
          <AgGridSkeleton height="696px" />
        )}
        <PageDrawer />
      </Col>
    </Row>
  );
};

export default Pages;
